import { createSelector } from 'reselect'

import gateway from '../gateway/gateway'
import { groupWithChildren } from '../services/groups'
import { Action as ActionType, Dispatch } from '../types/reducer'
import { Group, PrivacySetting } from '../types/group'
import { RootState, GroupsState as State } from '../types/state'
import { captureException } from '../sentry-config'

export const TOGGLE_GROUPS_LOADING = 'groups/TOGGLE_GROUPS_LOADING'
export const GET_GROUPS_SUCCESS = 'groups/GET_GROUPS_SUCCESS'

type ToggleGroupsLoadingAction = ActionType<typeof TOGGLE_GROUPS_LOADING, boolean>
type GetGroupsSuccessAction = ActionType<typeof GET_GROUPS_SUCCESS, Group[]>

type Action = ToggleGroupsLoadingAction | GetGroupsSuccessAction

const initialState: State = {
  loading: false,
  groups: [],
}

export default function groupsReducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case TOGGLE_GROUPS_LOADING:
      return {
        ...state,
        loading: action.payload,
      }
    case GET_GROUPS_SUCCESS:
      return {
        ...state,
        loading: false,
        groups: action.payload,
      }
    default:
      return state
  }
}

export const toggleGroupsLoading = (loading: boolean): ToggleGroupsLoadingAction => ({
  type: TOGGLE_GROUPS_LOADING,
  payload: loading,
})

export const getGroupsSuccess = (groups: Group[]): GetGroupsSuccessAction => ({
  type: GET_GROUPS_SUCCESS,
  payload: groups,
})

export const getGroups = () => async (
  dispatch: Dispatch<ToggleGroupsLoadingAction | GetGroupsSuccessAction, void>
): Promise<Group[]> => {
  dispatch(toggleGroupsLoading(true))

  try {
    const groups = await gateway.groups.getGroups()

    dispatch(getGroupsSuccess(groups))

    return groups
  } catch (e) {
    captureException(e)
  } finally {
    dispatch(toggleGroupsLoading(false))
  }

  return []
}

export const groupsSelector = createSelector(
  (state: RootState) => state.groups.groups,
  (groups: Group[]) => groups
)

export const visibleGroupsSelector = createSelector(
  groupsSelector,
  (state: RootState) => state.currentUser,
  (groups, { account, team }) => {
    if (!account?.team_admin && team.group_privacy_setting === PrivacySetting.GROUP_PUBLIC) {
      return account?.group_id ? groupWithChildren(groups, account?.group_id) : []
    }

    return groups
  }
)
